import { createFileRoute, Link } from "@tanstack/react-router";
import { listPublishedPosts } from "@/lib/blog-public.functions";
import { listForumCategories } from "@/lib/forum.functions";
import { listSpots } from "@/lib/camping.functions";
import { getFyndanuListings } from "@/lib/fyndanu.functions";
import { vehicleSlug } from "@/lib/vehicle-slug";
import { ORTER } from "@/data/seo-orter";
import { RAPIDO_MODELS } from "@/data/rapido-models";
import { PART_GUIDES } from "@/data/rapido-part-guides";
import { HUSBIL_BRANDS, HUSVAGN_BRANDS } from "@/data/vehicle-brands";
import { GLOSSARY } from "@/data/husbil-glossary";
import { COMPARISONS } from "@/data/husbil-comparisons";
import { BLOG_TOPICS } from "@/data/blog-topics";
import { assessOrtModell } from "@/lib/seo-families/ort-modell";

const CANONICAL = "https://www.akhusbilfritid.se/webbkarta";

type Item = { to: string; label: string; params?: Record<string, string> };

/** Ort × modell — samma kvalitetsurval som sitemapen, så inget indexerbart blir orphan. */
const ORT_MODELL: Item[] = ORTER.flatMap((o) =>
  RAPIDO_MODELS.filter((m) => assessOrtModell(o, m).passed).map((m) => ({
    to: "/husbil/$ort/$modell",
    params: { ort: o.slug, modell: m.slug },
    label: `${m.name} i ${o.name}`,
  })),
);

export const Route = createFileRoute("/webbkarta")({
  // Den stora länkdatan hör till sidans egen chunk, inte initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: async () => {
    const [posts, categories, spots, vehicles] = await Promise.all([
      listPublishedPosts().catch(() => ({ posts: [] as Array<{ slug: string; title: string }> })),
      listForumCategories().catch(() => [] as Array<{ slug: string; name: string }>),
      listSpots({ data: {} }).catch(() => ({ spots: [] as Array<{ slug: string; title: string }> })),
      getFyndanuListings().catch(() => ({ items: [] as any[] })),
    ]);
    return {
      posts: (posts?.posts ?? []).map((p: { slug: string; title: string }) => ({
        slug: p.slug,
        title: p.title,
      })),
      categories: (Array.isArray(categories) ? categories : []).map(
        (c: { slug: string; name: string }) => ({ slug: c.slug, name: c.name }),
      ),
      spots: ((spots?.spots ?? []) as Array<{ slug: string; title: string }>)
        .filter((s) => s.slug)
        .map((s) => ({ slug: s.slug, title: s.title })),
      vehicles: ((vehicles?.items ?? []) as any[]).map((v) => ({
        slug: vehicleSlug(v),
        title: v.title as string,
      })),
    };
  },


  head: () => ({
    meta: [
      { title: "Webbkarta – alla sidor hos AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Komplett webbkarta över AK Husbil & Fritid: fordon, Rapido-modeller, orter, guider, verktyg, ordlista, jämförelser, blogg och forum – allt på en sida.",
      },
      { property: "og:title", content: "Webbkarta – alla sidor hos AK Husbil & Fritid" },
      {
        property: "og:description",
        content: "Hitta varje sida på akhusbilfritid.se: fordon, Rapido, guider, verktyg, forum och blogg.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Webbkarta – AK Husbil & Fritid",
          url: CANONICAL,
          isPartOf: { "@type": "WebSite", url: "https://www.akhusbilfritid.se" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Webbkarta", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: Webbkarta,
});

function Section({ title, intro, items }: { title: string; intro?: string; items: Item[] }) {
  if (!items.length) return null;
  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <h2 className="text-lg font-semibold">{title}</h2>
      {intro && <p className="mt-1 text-sm text-muted-foreground">{intro}</p>}
      <ul className="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => (
          <li key={`${i.to}-${JSON.stringify(i.params ?? {})}-${i.label}`} className="text-sm">
            <Link
              to={i.to}
              params={i.params as never}
              className="text-foreground/85 underline-offset-4 transition hover:text-primary hover:underline"
            >
              {i.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Webbkarta() {
  const { posts, categories, spots, vehicles } = Route.useLoaderData();

  return (
    <main className="container-edge py-10">
      <nav aria-label="Brödsmulor" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:underline">
          Hem
        </Link>{" "}
        / <span className="text-foreground">Webbkarta</span>
      </nav>

      <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Webbkarta</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Varje sida på akhusbilfritid.se på ett ställe – fordon, Rapido-modeller, orter, guider,
        räknare, ordlista, jämförelser, blogg och forum. Sidan uppdateras automatiskt när nytt
        innehåll publiceras.
      </p>

      <div className="mt-8 grid gap-4">
        <Section
          title="Fordon & lager"
          intro="Live-lager och sökingångar."
          items={[
            { to: "/fordon", label: "Alla fordon (live)" },
            { to: "/husbilar-till-salu", label: "Husbilar till salu" },
            { to: "/husvagnar-till-salu", label: "Husvagnar till salu" },
            { to: "/husbil-matchning", label: "Husbil-matchning (6 frågor)" },
            { to: "/verktyg/passar-den-hos-mig", label: "Passar den hos mig? (mått, planvy & AR)" },
            { to: "/husbil-resa", label: "Bygg din husbil-resa (reseplanerare)" },
            { to: "/rapido/begagnade", label: "Begagnade Rapido" },
            { to: "/reservdelar", label: "Reservdelar" },
            { to: "/fritid", label: "Fritid & tillbehör" },
            ...vehicles.map((v: { slug: string; title: string }) => ({
              to: "/fordon/$id",
              params: { id: v.slug },
              label: v.title,
            })),
          ]}
        />


        <Section
          title="Rapido"
          intro="Officiell återförsäljare – modellprogram, priser och service."
          items={[
            { to: "/rapido", label: "Rapido – översikt" },
            { to: "/rapido/prislista", label: "Rapido prislista" },
            { to: "/rapido/aterforsaljare", label: "Rapido återförsäljare" },
            ...RAPIDO_MODELS.map((m) => ({
              to: "/rapido/modeller/$model",
              params: { model: m.slug },
              label: m.name,
            })),
          ]}
        />

        <Section
          title="Märkessidor"
          items={[
            ...HUSBIL_BRANDS.map((b) => ({
              to: "/husbilar/$brand",
              params: { brand: b.slug },
              label: `${b.name} husbil`,
            })),
            ...HUSVAGN_BRANDS.map((b) => ({
              to: "/husvagnar/$brand",
              params: { brand: b.slug },
              label: `${b.name} husvagn`,
            })),
          ]}
        />

        <Section
          title="Orter & regioner"
          intro="Husbil nära dig i norra Sverige."
          items={[
            { to: "/husbil-kalix", label: "Husbil Kalix" },
            { to: "/husvagn-kalix", label: "Husvagn Kalix" },
            { to: "/husbil-lulea", label: "Husbil Luleå" },
            { to: "/husbil-pitea", label: "Husbil Piteå" },
            { to: "/husbil-boden", label: "Husbil Boden" },
            { to: "/husbil-haparanda", label: "Husbil Haparanda" },
            { to: "/husbil-norrbotten", label: "Husbil Norrbotten" },
            ...ORTER.map((o) => ({
              to: "/husbil/$ort",
              params: { ort: o.slug },
              label: `Husbil ${o.name}`,
            })),
          ]}
        />

        <Section
          title="Marknadsdata"
          intro="Prisindex, prisstatistik, börsen och sålda fordon — allt samlat i marknadsrummet."
          items={[
            { to: "/marknad", label: "Marknadsrummet – all marknadsdata" },
            { to: "/prisindex", label: "AK Prisindex – prisutveckling månad för månad" },
            { to: "/prisstatistik-husbil", label: "Prisstatistik husbil & husvagn" },
            { to: "/borsen", label: "Fritidsbörsen – bolagen bakom märkena" },
            { to: "/salda", label: "Sålda fordon – arkiv med slutpriser" },
          ]}
        />

        <Section
          title="Verktyg & räknare"
          items={[
            { to: "/vardera-husbil", label: "Värdera husbil & husvagn" },
            { to: "/verktyg/manadskostnad-husbil", label: "Månadskostnad husbil" },
            { to: "/verktyg/bransleforbrukning-husbil", label: "Bränsleförbrukning" },
            { to: "/verktyg/avskrivning-husbil", label: "Avskrivning & värdeminskning" },
            { to: "/verktyg/korkort-husbil", label: "Körkortskollen" },
            { to: "/verktyg/fuktprotokoll", label: "Fuktprotokoll – 12 mätpunkter" },
          ]}
        />


        <Section
          title="Kunskap"
          intro="Guider, ordlista, jämförelser och fakta."
          items={[
            { to: "/guider", label: "Alla guider" },
            { to: "/tester", label: "Långtidstester av husbilar" },
            { to: "/stallplatser-husbil", label: "Ställplatser för husbil i Sverige" },
            { to: "/solceller-husbil", label: "Solceller & litiumbatteri i husbil" },
            { to: "/fukttest-husbil", label: "Fukttest av husbil & husvagn" },
            { to: "/b96-korkort", label: "B96-körkort – vikter, kostnad & prov" },
            { to: "/husbilsforsakring", label: "Husbilsförsäkring – pris & villkor" },
            { to: "/leasa-husbil", label: "Leasa husbil – leasing, avbetalning & moms" },
            { to: "/besiktning-husbil", label: "Besiktning av husbil" },
            { to: "/modeller", label: "Modellserier – priser & vanliga fel" },
            { to: "/ordlista", label: "Ordlista husbil & husvagn" },
            { to: "/jamfor", label: "Jämförelser" },
            { to: "/fakta", label: "Fakta & siffror" },
            { to: "/campingtips", label: "Campingtips" },
            ...GLOSSARY.slice(0, 40).map((g) => ({
              to: "/ordlista/$term",
              params: { term: g.slug },
              label: g.term,
            })),
            ...COMPARISONS.map((c) => ({
              to: "/jamfor/$slug",
              params: { slug: c.slug },
              label: `${c.aName} vs ${c.bName}`,
            })),
          ]}
        />

        <Section
          title="Blogg"
          intro={`${posts.length} publicerade inlägg och ${BLOG_TOPICS.length} ämnesingångar.`}
          items={[
            { to: "/blogg", label: "Bloggen – start" },
            ...BLOG_TOPICS.map((t) => ({
              to: "/blogg/amne/$topic",
              params: { topic: t.id },
              label: `Ämne: ${t.label}`,
            })),
            ...posts.map((p: { slug: string; title: string }) => ({
              to: "/blogg/$slug",
              params: { slug: p.slug },
              label: p.title,
            })),
          ]}
        />

        <Section
          title="Rapido verkstadsguider"
          intro={`${PART_GUIDES.length} felsökningsguider – symptom, delnummer, tid och pris.`}
          items={PART_GUIDES.map((g) => ({
            to: "/rapido/guider/$slug",
            params: { slug: g.slug },
            label: g.title,
          }))}
        />

        <Section
          title="Husbil i din ort × Rapido-modell"
          intro="Unika sidor med vikt, längd och restid räknat från varje ort."
          items={ORT_MODELL}
        />

        <Section
          title="Campingtips & ställplatser"
          intro={`${spots.length} platser med bilder, karta och kommentarer.`}
          items={[
            { to: "/campingtips", label: "Alla campingtips" },
            ...spots.map((s: { slug: string; title: string }) => ({
              to: "/campingtips/$slug",
              params: { slug: s.slug },
              label: s.title,
            })),
          ]}
        />



        <Section
          title="Forum"
          intro="Fritidsforum – frågor och svar mellan husbils- och husvagnsägare."
          items={[
            { to: "/forum", label: "Forum – start" },
            { to: "/erfarenheter", label: "Fukt- & gasolrapporter (erfarenhetsdatabas)" },

            ...categories.map((c: { slug: string; name: string }) => ({
              to: "/forum/k/$slug",
              params: { slug: c.slug },
              label: c.name,
            })),
          ]}
        />

        <Section
          title="Suomeksi / På finska"
          intro="Finsk minisajt för kunder från Tornio, Kemi och Oulu."
          items={[
            { to: "/fi", label: "Etusivu (suomeksi)" },
            { to: "/fi/asuntoautot", label: "Asuntoautot" },
            { to: "/fi/asuntovaunut", label: "Asuntovaunut" },
            { to: "/fi/rapido", label: "Rapido suomeksi" },
            { to: "/fi/tuonti", label: "Tuonti & autovero (laskuri)" },
            { to: "/fi/ostajan-opas", label: "Ostajan opas" },
            { to: "/fi/kosteusmittaus", label: "Kosteusmittaus ja kaasutarkastus" },
            { to: "/fi/yhteystiedot", label: "Yhteystiedot" },
          ]}
        />

        <Section
          title="Om oss & villkor"
          items={[
            { to: "/om-oss", label: "Om oss" },
            { to: "/kontakt", label: "Kontakt" },
            { to: "/integritetspolicy", label: "Integritetspolicy" },
            { to: "/cookies", label: "Cookies" },
            { to: "/anvandarvillkor", label: "Användarvillkor" },
          ]}
        />
      </div>

      <p className="mt-8 text-xs text-muted-foreground">
        Maskinläsbara versioner: <a href="/sitemap.xml" className="underline">sitemap.xml</a> ·{" "}
        <a href="/llms.txt" className="underline">llms.txt</a> ·{" "}
        <a href="/rss.xml" className="underline">rss.xml</a>
      </p>
    </main>
  );
}
